import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronDownIcon } from '@/components/icons';
import { getSignedMediaUrl, formatCurrencyForDisplay } from '@/lib/utils';
import { formatDateByTimezone } from '@/lib/utils';
import type { AddOnConflict, AddOnResponse } from '@/features/cart/types';
import { getAddOnById } from '@/features/cart/utils/cartItemUtils';


interface AddOnConflictBannerProps {
  addOnConflicts: AddOnConflict[];
  addOns: AddOnResponse[];
  clubHandle?: string | null;
  /** Currency code for price formatting (e.g., 'GBP', 'USD') */
  currency?: string;
}

const AddOnConflictBanner: React.FC<AddOnConflictBannerProps> = ({
  addOnConflicts,
  addOns,
  currency,
}) => {
  const [expandedAddOnId, setExpandedAddOnId] = useState<string | null>(null);
  // Format price using currency if provided, otherwise show raw amount
  const formatPrice = (amount: number) => currency 
    ? formatCurrencyForDisplay(amount, currency) 
    : `${(amount / 100).toFixed(2)}`;

  // Group conflicts by unique add-on ID
  const groupedConflicts = useMemo(() => {
    const grouped: Record<string, AddOnConflict[]> = {};
    addOnConflicts.forEach(conflict => {
      if (!grouped[conflict.add_on_id]) {
        grouped[conflict.add_on_id] = [];
      }
      grouped[conflict.add_on_id].push(conflict);
    });
    return grouped;
  }, [addOnConflicts]);

  const uniqueAddOnIds = Object.keys(groupedConflicts);

  if (uniqueAddOnIds.length === 0) {
    return null;
  }

  return (
    <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mb-6">
      <div className="mb-4">
        <h3 className="text-orange-400 font-semibold text-sm mb-1">
          Add-On Inventory Shortage
        </h3>
        <p className="text-gray-300 text-sm">
          Not enough quantity available for the following add-ons
        </p>
      </div>

      {/* Add-on Cards Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
        {uniqueAddOnIds.map((addOnId) => {
          const addOnData = getAddOnById(addOns, addOnId);
          if (!addOnData) return null;

          const conflicts = groupedConflicts[addOnId];
          const hasValidImage = addOnData.image_id && addOnData.image_id !== '00000000-0000-0000-0000-000000000000';
          const imageUrl = hasValidImage ? getSignedMediaUrl(addOnData.image_id, 'image').url : null;
          
          // Use default_price or 0 if not available
          const price = addOnData.default_price ?? 0;
          const isExpanded = expandedAddOnId === addOnId;

          return (
            <div key={addOnId} className="relative">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setExpandedAddOnId(isExpanded ? null : addOnId)}
                className={`relative aspect-[4/3] rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-orange-500/50 ${
                  isExpanded ? 'ring-2 ring-orange-500/50' : ''
                }`}
                style={{
                  background: imageUrl 
                    ? `url(${imageUrl})`
                    : 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
                
                {/* Content */}
                <div className="absolute inset-0 flex flex-col justify-between p-2.5">
                  <div className="flex-1" />
                  <div>
                    <h5 className="text-white font-semibold text-sm mb-0.5 line-clamp-1">
                      {addOnData.name}
                    </h5>
                    <p className="text-orange-400 font-bold text-sm">
                      {formatPrice(price)}
                    </p>
                  </div>
                </div>

                {/* Conflict Indicator */}
                <div className="absolute top-2 right-2 z-10 bg-red-500/80 rounded-full px-1.5 py-0.5">
                  <span className="text-white text-xs font-medium">
                    {conflicts.length} conflict{conflicts.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </motion.div>

              {/* Expanded Dropdown */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0, marginTop: 0 }}
                    animate={{ opacity: 1, height: 'auto', marginTop: 8 }}
                    exit={{ opacity: 0, height: 0, marginTop: 0 }}
                    className="overflow-hidden"
                  >
                    <div 
                      className="relative rounded-xl overflow-hidden border border-orange-500/20"
                      style={{
                        background: 'rgba(0, 0, 0, 0.6)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                      }}
                    >
                      <div className="relative p-4 space-y-3">
                        <div className="flex items-center justify-between mb-3 pb-3 border-b border-orange-500/20">
                          <h6 className="text-white font-semibold text-base">Event Conflicts:</h6>
                          <ChevronDownIcon className="w-4 h-4 text-orange-400 rotate-180" />
                        </div>
                        {conflicts.map((conflict, index) => {
                          const eventDate = formatDateByTimezone(
                            conflict.event_date,
                            'UTC',
                            { format: 'date' }
                          );
                          return (
                            <motion.div
                              key={index}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.05 }}
                              className="relative rounded-lg overflow-hidden border border-gray-700/50"
                              style={{
                                background: 'rgba(31, 41, 55, 0.5)',
                                backdropFilter: 'blur(10px)',
                                WebkitBackdropFilter: 'blur(10px)',
                              }}
                            >
                              <div className="p-4">
                                <div className="flex items-start justify-between gap-4 mb-3">
                                  <div className="flex-1">
                                    <h5 className="text-white font-semibold text-base mb-2">
                                      {conflict.event_name}
                                    </h5>
                                    <div className="flex items-center gap-3 text-sm text-gray-300">
                                      <div className="flex items-center gap-1.5">
                                        <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <span className="font-medium">{eventDate}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between pt-3 border-t border-gray-700/50">
                                  <span className="text-xs text-gray-400">Inventory Status:</span>
                                  <div className="flex items-center gap-3 text-xs">
                                    <span className="text-gray-300">
                                      Requested: <span className="font-semibold text-white">{conflict.requested_quantity}</span>
                                    </span>
                                    <span className="text-gray-500">•</span>
                                    <span className="text-orange-400">
                                      Available: <span className="font-semibold">{conflict.available_quantity}</span>
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AddOnConflictBanner;

